import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface AffiliateSummary {
  id: number;
  affiliate_code: string;
  display_name: string | null;
  status: string;
  commission_type: 'percentage' | 'flat_rate' | string;
  commission_value: string;
  payment_schedule: string;
  payment_limit: number | null;
  stripe_account_id: string | null;
  onboarding_completed: boolean;
  affiliate_level: number;
  parent_affiliate_id: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  referral_count: number;
  converted_count: number;
  commission_paid: string;
  commission_reversed: string;
  commission_pending: string;
  commission_failed: string;
  commission_count: number;
}

export interface AffiliateCommission {
  id: number;
  user_id: number | null;
  commission_amount: string;
  /** How much of commission_amount a refund or lost dispute took back. */
  reversed_amount: string;
  commission_level: number;
  status: string;
  stripe_transfer_id: string | null;
  source_transaction_id: string | null;
  stripe_subscription_id: string | null;
  total_payment_amount: string;
  payment_type: string;
  payment_number: number | null;
  payment_date: string;
  /** Failed, or left pending by an interrupted payout: the admin can retry it. */
  retriable?: boolean;
}

export interface AffiliateReferral {
  id: number;
  user_id: number;
  tracking_code: string;
  converted_at: string | null;
  subscription_id: number | null;
  created_at: string;
}

export interface AffiliateDetail extends Omit<AffiliateSummary, 'referral_count' | 'converted_count' | 'commission_paid' | 'commission_reversed' | 'commission_pending' | 'commission_failed' | 'commission_count'> {
  commissions: AffiliateCommission[];
  referrals: AffiliateReferral[];
  commissionSummary: {
    paid: { count: number; total: number };
    pending: { count: number; total: number };
    failed: { count: number; total: number };
    reversed: { count: number; total: number };
  };
}

export function useAffiliates() {
  return useQuery({
    queryKey: ['affiliates'],
    queryFn: () => apiClient<AffiliateSummary[]>('/affiliates'),
  });
}

export function useAffiliate(id: string) {
  return useQuery({
    queryKey: ['affiliates', id],
    queryFn: () => apiClient<AffiliateDetail>(`/affiliates/${id}`),
  });
}

export interface AffiliateDraft {
  id?: number;
  affiliate_code?: string;
  display_name?: string | null;
  commission_type?: string;
  commission_value?: number | string;
  payment_schedule?: string;
  payment_limit?: number | null;
  status?: string;
  notes?: string | null;
  parent_affiliate_id?: number | null;
  stripe_account_id?: string | null;
}

export function useCreateAffiliate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draft: AffiliateDraft) => apiClient<AffiliateSummary>('/affiliates', { method: 'POST', body: JSON.stringify(draft) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['affiliates'] }),
  });
}

export function useUpdateAffiliate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, draft }: { id: number; draft: AffiliateDraft }) =>
      apiClient<AffiliateSummary>(`/affiliates/${id}`, { method: 'PUT', body: JSON.stringify(draft) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['affiliates'] }),
  });
}

export interface StripeConnectStatus {
  hasStripeAccount: boolean;
  id?: string;
  details_submitted?: boolean;
  charges_enabled?: boolean;
  payouts_enabled?: boolean;
  onboarding_completed?: boolean;
  transfers_active?: boolean;
}

export function useStripeConnectStatus(id: string) {
  return useQuery({
    queryKey: ['affiliates', id, 'stripe-connect'],
    queryFn: () => apiClient<StripeConnectStatus>(`/affiliates/${id}/stripe-connect`),
  });
}

export function useStartStripeOnboarding() {
  return useMutation({
    mutationFn: (id: number) => apiClient<{ onboardingUrl: string; stripeAccountId: string }>(`/affiliates/${id}/stripe-connect`, { method: 'POST' }),
  });
}

export function useRetryCommission(affiliateId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commissionId: number) =>
      apiClient(`/affiliates/${affiliateId}/commissions/${commissionId}/retry`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['affiliates', affiliateId] }),
  });
}
